/**
 * notificacionesApi — Referencias tipadas a las Cloud Functions del módulo de
 * notificaciones (callables `portal_*`) + subida de imagen a Storage.
 */
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { functions, storage } from '../../config/firebase';
import type {
  CampanaPayload,
  ContarDispositivosResult,
  ContenidoPrueba,
  CrearCampanaResult,
  EnviarCampanaResult,
  EnviarPruebaResult,
  GestionarDispositivosResult,
} from './tiposCampana';

// ─── Callables ───────────────────────────────────────────────

export const crearCampanaFn = httpsCallable<CampanaPayload, CrearCampanaResult>(
  functions,
  'portal_crearCampana',
);

export const actualizarCampanaFn = httpsCallable<
  CampanaPayload & { campanaId: string },
  CrearCampanaResult
>(functions, 'portal_actualizarCampana');

export const cancelarCampanaFn = httpsCallable<{ campanaId: string }, unknown>(
  functions,
  'portal_cancelarCampana',
);

export const eliminarCampanaFn = httpsCallable<{ campanaId: string }, { success: boolean }>(
  functions,
  'portal_eliminarCampana',
);

export const archivarCampanaFn = httpsCallable<
  { campanaId: string; archivar: boolean },
  { success: boolean; archivada: boolean }
>(functions, 'portal_archivarCampana');

export const duplicarCampanaFn = httpsCallable<{ campanaId: string }, { campanaId: string }>(
  functions,
  'portal_duplicarCampana',
);

export const enviarCampanaFn = httpsCallable<{ campanaId: string }, EnviarCampanaResult>(
  functions,
  'portal_enviarCampana',
);

const contarDispositivosFn = httpsCallable<undefined, ContarDispositivosResult>(
  functions,
  'portal_contarDispositivos',
);

export const gestionarDispositivosPruebaFn = httpsCallable<
  { action: 'listar' | 'agregar' | 'quitar'; cedula?: string; uid?: string },
  GestionarDispositivosResult
>(functions, 'portal_gestionarDispositivosPrueba');

export const enviarPruebaFn = httpsCallable<
  { campanaId: string } | { contenido: ContenidoPrueba },
  EnviarPruebaResult
>(functions, 'portal_enviarPrueba');

// ─── Conteo de dispositivos con caché en memoria (5 min) ────

let cacheDispositivos: { total: number; ts: number } | null = null;
const CACHE_TTL_MS = 5 * 60_000;

export async function contarDispositivos(): Promise<number> {
  if (cacheDispositivos && Date.now() - cacheDispositivos.ts < CACHE_TTL_MS) {
    return cacheDispositivos.total;
  }
  const res = await contarDispositivosFn();
  cacheDispositivos = { total: res.data.total, ts: Date.now() };
  return res.data.total;
}

// ─── Storage: imagen de campaña ──────────────────────────────

export async function subirImagenCampana(
  campanaId: string,
  blob: Blob,
): Promise<{ url: string; path: string }> {
  const path = `campanas/${campanaId}/imagen.jpg`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: 'image/jpeg',
    cacheControl: 'public,max-age=31536000',
  });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

// ─── Helper de errores ───────────────────────────────────────

export function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
